import { Button } from "@bednbody/ui/components/button";
import { useAsyncAction } from "@bednbody/ui/hooks/use-async-action";

/**
 * The "Try again" on a failed read.
 *
 * Repeating a read is harmless, which is why these were the last controls to
 * get any feedback — but a retry that looks inert is why people press it four
 * times and then say the app is broken. This spins for as long as the refetch
 * takes and refuses the extra presses.
 *
 * A component rather than the `useAsyncAction` hook at each call site: most of
 * these buttons live inside a nested error branch, where a hook cannot go.
 */
export function RetryButton({
  onRetry,
  children = "Try again",
  size = "lg",
  variant = "outline",
  className,
}: {
  onRetry: () => Promise<unknown>;
  children?: React.ReactNode;
  size?: React.ComponentProps<typeof Button>["size"];
  variant?: React.ComponentProps<typeof Button>["variant"];
  className?: string;
}) {
  const retry = useAsyncAction(onRetry);
  return (
    <Button
      size={size}
      variant={variant}
      className={className}
      loading={retry.busy}
      onClick={() => void retry.run()}
    >
      {children}
    </Button>
  );
}
